import { useMemo } from "react";
import { playSoft } from "@/audio/piano";

const WHITE_NOTES = ["C", "D", "E", "F", "G", "A", "B"];
const BLACK_OFFSETS: Record<number, string> = { 0: "C#", 1: "D#", 3: "F#", 4: "G#", 5: "A#" };

// Flat → sharp normalisation so lessons can use either notation in `pitch`.
// Db→C#, Eb→D#, Gb→F#, Ab→G#, Bb→A#. Octave digit preserved.
const FLAT_TO_SHARP: Record<string, string> = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };
function normalisePitch(p: string): string {
  const m = p.match(/^([A-G])(b|#)?(-?\d+)$/);
  if (!m) return p;
  const [, letter, accidental, oct] = m;
  if (accidental === "b") {
    const key = letter + "b";
    const replaced = FLAT_TO_SHARP[key];
    if (replaced) return replaced + oct;
  }
  return p;
}

export interface KeyboardProps {
  /** Lowest visible C octave (e.g. 3 = C3). */
  startOctave?: number;
  /** Number of octaves to render. */
  octaves?: number;
  /** Pitch classes to mark with a colored dot (e.g. ["C","E","G"]). */
  highlights?: { pitch: string; color: string; label?: string }[];
  /** Click handler. */
  onPlay?: (pitch: string) => void;
  height?: number;
}

export function Keyboard({
  startOctave = 3,
  octaves = 2,
  highlights = [],
  onPlay,
  height = 140,
}: KeyboardProps) {
  const totalWhites = octaves * 7;
  const whiteW = 100 / totalWhites;

  const whites = useMemo(() => {
    const arr: { pitch: string; idx: number }[] = [];
    for (let o = 0; o < octaves; o++) {
      WHITE_NOTES.forEach((n, i) => arr.push({ pitch: `${n}${startOctave + o}`, idx: o * 7 + i }));
    }
    return arr;
  }, [octaves, startOctave]);

  const blacks = useMemo(() => {
    const arr: { pitch: string; left: number }[] = [];
    for (let o = 0; o < octaves; o++) {
      WHITE_NOTES.forEach((_, i) => {
        const pc = BLACK_OFFSETS[i];
        if (!pc) return;
        const xWhite = (o * 7 + i) * whiteW;
        arr.push({ pitch: `${pc}${startOctave + o}`, left: xWhite + whiteW * 0.7 });
      });
    }
    return arr;
  }, [octaves, startOctave, whiteW]);

  const findHL = (pitch: string) =>
    highlights.find((h) => normalisePitch(h.pitch) === pitch);

  const trigger = async (p: string) => {
    onPlay?.(p);
    await playSoft(p, "8n");
  };

  return (
    <div className="relative w-full select-none overflow-hidden rounded-xl border border-nsm-border bg-zinc-900" style={{ height }}>
      {/* whites */}
      <div className="flex h-full">
        {whites.map((w) => {
          const hl = findHL(w.pitch);
          return (
            <button
              key={w.pitch}
              onClick={() => trigger(w.pitch)}
              className="relative h-full border-r border-zinc-800 bg-zinc-50 text-zinc-700 transition active:bg-amber-200"
              style={{ width: `${whiteW}%` }}
            >
              {hl && (
                <span
                  className="absolute bottom-2 left-1/2 inline-flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full text-[10px] font-bold text-white"
                  style={{ background: hl.color }}
                >
                  {hl.label ?? ""}
                </span>
              )}
              <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-semibold uppercase text-zinc-400">
                {w.pitch.replace(/-?\d+$/, "")}
              </span>
            </button>
          );
        })}
      </div>
      {/* blacks (absolute over whites). The row itself must let clicks through:
          it spans the full width, so without pointer-events-none it swallowed every
          tap on the top 60% of the white keys and only their lower part played. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[60%]">
        {blacks.map((b) => {
          const hl = findHL(b.pitch);
          return (
            <button
              key={b.pitch}
              onClick={() => trigger(b.pitch)}
              className="pointer-events-auto absolute top-0 rounded-b-md border border-zinc-700 bg-zinc-900 transition active:bg-amber-700"
              style={{ left: `${b.left}%`, width: `${whiteW * 0.6}%`, height: "100%" }}
            >
              {hl && (
                <span
                  className="absolute bottom-1 left-1/2 inline-flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full text-[9px] font-bold text-white"
                  style={{ background: hl.color }}
                >
                  {hl.label ?? ""}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
